import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Body1,
  Button,
  MessageBar,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { hasAnyRole } from '@lg/shared';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PEOPLE_COLOR, STATUS_COLOR, STATUS_LABEL, errorMessage, shortDate } from '../ui/format';

const useStyles = makeStyles({
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  title: { marginRight: 'auto' },
  intro: { display: 'block', marginBottom: tokens.spacingVerticalL, color: tokens.colorNeutralForeground2 },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalS,
    boxShadow: tokens.shadow2,
  },
  row: { cursor: 'pointer' },
  mono: { fontFamily: tokens.fontFamilyMonospace },
});

export function RequestsPage() {
  const styles = useStyles();
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const requests = useQuery({ queryKey: ['requests'], queryFn: () => api.listRequests() });

  return (
    <div>
      <div className={styles.head}>
        <Title2 as="h1" className={styles.title}>
          Venue requests
        </Title2>
        {hasAnyRole(user, ['Requester']) && (
          <Button appearance="primary" onClick={() => navigate('/requests/new')}>
            New request
          </Button>
        )}
      </div>
      <Body1 className={styles.intro}>
        Each request turns a signed venue contract into a ready-to-transact legal entity in Dynamics 365:
        intake, people and approvals, four readiness gates, then provisioning from the template company.
      </Body1>
      {requests.isPending && <Spinner label="Loading requests…" />}
      {requests.isError && <MessageBar intent="error">{errorMessage(requests.error)}</MessageBar>}
      {requests.data && (
        <div className={styles.card}>
          <Table aria-label="Venue requests">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '24%' }}>Venue</TableHeaderCell>
                <TableHeaderCell style={{ width: '9%' }}>Legal entity</TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>Status</TableHeaderCell>
                <TableHeaderCell style={{ width: '6%' }}>Gate</TableHeaderCell>
                <TableHeaderCell style={{ width: '10%' }}>People</TableHeaderCell>
                <TableHeaderCell style={{ width: '18%' }}>Requester</TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>Go-live</TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>Updated</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.data.map((r) => (
                <TableRow
                  key={r.id}
                  className={styles.row}
                  onClick={() => navigate(`/requests/${r.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/requests/${r.id}`)}
                >
                  <TableCell>
                    <b>{r.venueName}</b>
                  </TableCell>
                  <TableCell className={styles.mono}>{r.legalEntityId ?? '—'}</TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={STATUS_COLOR[r.status]}>
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.currentGate}</TableCell>
                  <TableCell>
                    {r.peopleStatus ? (
                      <Badge appearance="outline" color={PEOPLE_COLOR[r.peopleStatus]}>
                        {r.peopleStatus}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <TableCellLayout truncate>{r.requesterUpn}</TableCellLayout>
                  </TableCell>
                  <TableCell>{shortDate(r.targetGoLive)}</TableCell>
                  <TableCell>{shortDate(r.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
